import { useState, useEffect } from "react";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { SNOOZE_DEFAULTS, allSnoozeKeys, categoryChipCls, categoryDotCls, CATEGORY_COLOR_KEYS } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Settings, Clock, Trash2, Plus, GripVertical, MessageSquare, Bell, BellOff, Tag, Ruler, Check, UserRound, LogOut, BarChart3, Users, Sprout, CheckCircle2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";

const NUDGE_OPTS = [
  { key: "toast", label: "In-app toast", icon: MessageSquare },
  { key: "browser", label: "Browser notification", icon: Bell },
  { key: "off", label: "Stay quiet", icon: BellOff },
];

const TABS = [
  { key: "account", label: "Account", icon: UserRound },
  { key: "length", label: "Root length", icon: Ruler },
  { key: "alerts", label: "Alerts", icon: Bell },
  { key: "snooze", label: "Snooze", icon: Clock },
  { key: "categories", label: "Categories", icon: Tag },
  { key: "stats", label: "Stats", icon: BarChart3, adminOnly: true },
  { key: "feedback", label: "Feedback", icon: MessageSquare, adminOnly: true },
];

const clone = (o) => JSON.parse(JSON.stringify(o || {}));

export default function SettingsDialog() {
  const { settings, saveSettings, open, setOpen } = useSettings();
  const { user, setUser, logout } = useAuth();
  const [draft, setDraft] = useState(() => clone(settings));
  const [baseline, setBaseline] = useState("");
  const [activeTab, setActiveTab] = useState("account");
  const [dragKey, setDragKey] = useState(null);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [feedbackItems, setFeedbackItems] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackFilter, setFeedbackFilter] = useState("all");
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);

  // add-form state
  const [kind, setKind] = useState("relative");
  const [amount, setAmount] = useState("3");
  const [unit, setUnit] = useState("hours");
  const [daysAhead, setDaysAhead] = useState("1");
  const [time, setTime] = useState("09:00");
  const [label, setLabel] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [newCatColor, setNewCatColor] = useState("orange");

  // Sync a fresh draft snapshot whenever the dialog opens
  useEffect(() => {
    if (open) {
      const snap = clone(settings);
      setDraft(snap);
      setBaseline(JSON.stringify(snap));
      setActiveTab("account");
      setNameDraft(user?.display_name || user?.name || "");
      setFeedbackItems(null);
      setStatsData(null);
    }
  }, [open, settings, user]);

  // Load stats when an admin opens the Stats tab
  useEffect(() => {
    if (open && activeTab === "stats" && user?.is_admin && statsData === null && !statsLoading) {
      setStatsLoading(true);
      api.get("/admin/stats")
        .then((r) => setStatsData(r.data))
        .catch(() => setStatsData({}))
        .finally(() => setStatsLoading(false));
    }
  }, [open, activeTab, user, statsData, statsLoading]);

  // Load feedback when an admin opens the Feedback tab
  useEffect(() => {
    if (open && activeTab === "feedback" && user?.is_admin && feedbackItems === null && !feedbackLoading) {
      setFeedbackLoading(true);
      api.get("/admin/feedback")
        .then((r) => setFeedbackItems(r.data.items || []))
        .catch(() => setFeedbackItems([]))
        .finally(() => setFeedbackLoading(false));
    }
  }, [open, activeTab, user, feedbackItems, feedbackLoading]);

  const visibleTabs = TABS.filter((t) => !t.adminOnly || user?.is_admin);

  const saveName = async () => {
    const val = nameDraft.trim();
    setSavingName(true);
    try {
      const r = await api.put("/account", { display_name: val });
      setUser(r.data);
      toast.success("Name updated");
    } catch {
      toast.error("Couldn't update your name");
    } finally {
      setSavingName(false);
    }
  };

  const setFeedbackStatus = async (id, status) => {
    setFeedbackItems((items) => (items || []).map((f) => (f.id === id ? { ...f, status } : f)));
    try {
      await api.patch(`/admin/feedback/${id}`, { status });
    } catch {
      toast.error("Couldn't update status");
    }
  };

  const dirty = JSON.stringify(draft) !== baseline;

  const snooze = draft?.snooze || { disabled: [], custom: [], order: [] };
  const disabled = snooze.disabled || [];
  const custom = snooze.custom || [];
  const nudge = draft?.nudge || "toast";
  const maxCodeLen = draft?.max_code_len ?? 24;
  const categories = draft?.categories || [];

  const byKey = {};
  SNOOZE_DEFAULTS.forEach((o) => { byKey[o.key] = { key: o.key, label: o.label, isCustom: false }; });
  custom.forEach((c) => { byKey[`c_${c.id}`] = { key: `c_${c.id}`, label: c.label, isCustom: true, raw: c }; });
  const orderedKeys = allSnoozeKeys(draft).filter((k) => byKey[k]);

  const patchSnooze = (patch) => setDraft((d) => ({ ...d, snooze: { ...(d.snooze || {}), ...patch } }));

  const toggleDefault = (key, enabled) =>
    patchSnooze({ disabled: enabled ? disabled.filter((k) => k !== key) : [...disabled, key] });

  const removeCustom = (id) =>
    patchSnooze({ custom: custom.filter((c) => c.id !== id), order: (snooze.order || []).filter((k) => k !== `c_${id}`) });

  const onDrop = (targetKey) => {
    if (!dragKey || dragKey === targetKey) return setDragKey(null);
    const list = [...orderedKeys];
    const from = list.indexOf(dragKey);
    const to = list.indexOf(targetKey);
    list.splice(to, 0, list.splice(from, 1)[0]);
    patchSnooze({ order: list });
    setDragKey(null);
  };

  const setNudge = (mode) => {
    if (mode === "browser" && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    setDraft((d) => ({ ...d, nudge: mode }));
  };

  const addCategoryLocal = () => {
    const name = newCategory.trim();
    if (!name) return toast.error("Enter a category name");
    if (categories.some((c) => c.name.toLowerCase() === name.toLowerCase())) return toast.error("That category already exists");
    setDraft((d) => ({ ...d, categories: [...(d.categories || []), { id: crypto.randomUUID().slice(0, 8), name, color: newCatColor }] }));
    setNewCategory(""); setNewCatColor("orange");
  };
  const removeCategory = (id) => setDraft((d) => ({ ...d, categories: (d.categories || []).filter((c) => c.id !== id) }));

  const autoLabel = () => {
    if (kind === "relative") {
      const n = Number(amount) || 0;
      return `In ${n} ${unit === "minutes" ? "min" : unit === "hours" ? (n === 1 ? "hour" : "hours") : (n === 1 ? "day" : "days")}`;
    }
    const [h, m] = time.split(":").map(Number);
    const d = Number(daysAhead) || 0;
    const when = d === 0 ? "Today" : d === 1 ? "Tomorrow" : `In ${d} days`;
    const t = new Date(); t.setHours(h, m, 0, 0);
    return `${when} ${t.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  };

  const addCustom = () => {
    let c;
    if (kind === "relative") {
      const amt = Number(amount);
      if (!amt || amt <= 0) return toast.error("Enter a valid amount");
      c = { id: crypto.randomUUID().slice(0, 8), kind: "relative", amount: amt, unit, label: label.trim() || autoLabel() };
    } else {
      const [h, m] = time.split(":").map(Number);
      const d = Number(daysAhead);
      if (Number.isNaN(d) || d < 0) return toast.error("Enter valid days ahead");
      c = { id: crypto.randomUUID().slice(0, 8), kind: "time", daysAhead: d, hour: h, minute: m, label: label.trim() || autoLabel() };
    }
    patchSnooze({ custom: [...custom, c] });
    setLabel("");
    toast.success("Custom snooze added to draft — Save to keep it");
  };

  const handleSave = async () => {
    await saveSettings(draft);
    toast.success("Settings saved");
    setConfirmOpen(false);
    setOpen(false);
  };

  const attemptClose = () => {
    if (dirty) setConfirmOpen(true);
    else setOpen(false);
  };

  const discardAndClose = () => {
    setConfirmOpen(false);
    setOpen(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : attemptClose())}>
        <DialogTrigger asChild>
          <Button size="icon" variant="ghost" data-testid="settings-gear" title="Settings" className="h-9 w-9 rounded-full text-muted-foreground">
            <Settings className="w-5 h-5" />
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden block" data-testid="settings-dialog">
          <DialogHeader className="px-6 pt-6 pb-3">
            <DialogTitle className="text-xl font-extrabold flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" /> Settings
            </DialogTitle>
            <DialogDescription>Personalize how Carrot works for you.</DialogDescription>
          </DialogHeader>

          {/* Tabs */}
          <div className="px-4 border-b border-border">
            <div className="flex items-center gap-1 overflow-x-auto no-scrollbar" data-testid="settings-tabs">
              {visibleTabs.map((t) => (
                <button
                  key={t.key}
                  data-testid={`settings-tab-${t.key}`}
                  onClick={() => setActiveTab(t.key)}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                    activeTab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <t.icon className="w-4 h-4" /> {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab content */}
          <div className="px-6 py-5 max-h-[55vh] overflow-y-auto">
            {activeTab === "account" && (
              <div className="space-y-5" data-testid="tab-panel-account">
                <div className="flex items-center gap-4">
                  <Avatar className="w-16 h-16 border-2 border-primary/30">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-primary/10 text-primary font-bold text-xl">
                      {user?.name?.[0]?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="text-lg font-bold truncate" data-testid="account-name">{user?.display_name || user?.name}</div>
                    <div className="text-sm text-muted-foreground truncate" data-testid="account-email">{user?.email}</div>
                    <div className="mt-1 inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary">
                      Beta · Pro unlocked
                    </div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Your email and photo come from your Google account. Your display name is what teammates see in workspaces.
                </p>
                <div className="space-y-1.5">
                  <Label htmlFor="display-name" className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Display name</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="display-name"
                      data-testid="account-name-input"
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      placeholder={user?.name || "Your name"}
                      maxLength={60}
                    />
                    <Button
                      onClick={saveName}
                      disabled={savingName || !nameDraft.trim() || nameDraft.trim() === (user?.display_name || user?.name || "")}
                      data-testid="account-name-save-btn"
                      className="rounded-full bg-primary hover:bg-[#E85D04] text-white font-semibold shrink-0"
                    >
                      {savingName ? "Saving…" : "Save"}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Shown in workspace member lists and on assigned roots.</p>
                </div>
                <Button
                  variant="outline"
                  onClick={logout}
                  data-testid="account-logout-btn"
                  className="rounded-full gap-1.5 text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/5"
                >
                  <LogOut className="w-4 h-4" /> Sign out
                </Button>
              </div>
            )}
            {activeTab === "stats" && user?.is_admin && (
              <div className="space-y-4" data-testid="tab-panel-stats">
                {statsLoading && <p className="text-sm text-muted-foreground" data-testid="stats-loading">Loading…</p>}
                {!statsLoading && statsData && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { icon: Users, label: "Total users", value: statsData.total_users ?? 0, testid: "stat-total-users" },
                        { icon: Users, label: "New this week", value: statsData.new_week ?? 0, sub: `${statsData.new_today ?? 0} today`, testid: "stat-new-week" },
                        { icon: Sprout, label: "Roots planted", value: statsData.total_roots ?? 0, testid: "stat-total-roots" },
                        { icon: CheckCircle2, label: "Roots completed", value: statsData.completed_roots ?? 0, testid: "stat-completed-roots" },
                      ].map((s) => {
                        const Icon = s.icon;
                        return (
                          <div key={s.label} data-testid={s.testid} className="rounded-xl border border-border p-3.5">
                            <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                              <Icon className="w-3.5 h-3.5" />
                              <span className="text-xs font-semibold uppercase tracking-wide">{s.label}</span>
                            </div>
                            <div className="text-2xl font-black">{s.value}</div>
                            {s.sub && <div className="text-xs text-muted-foreground mt-0.5">{s.sub}</div>}
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground">Counts exclude internal test/demo accounts.</p>
                    <div>
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Recent signups</div>
                      {(statsData.recent_users || []).length === 0 ? (
                        <p className="text-sm text-muted-foreground" data-testid="stats-recent-empty">No signups yet.</p>
                      ) : (
                        <div className="space-y-1.5" data-testid="stats-recent-list">
                          {statsData.recent_users.map((u, i) => (
                            <div key={i} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2">
                              <div className="min-w-0">
                                <div className="text-sm font-semibold truncate">{u.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                              </div>
                              <span className="text-xs text-muted-foreground shrink-0">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            {activeTab === "feedback" && user?.is_admin && (
              <div className="space-y-3" data-testid="tab-panel-feedback">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-sm text-muted-foreground">Beta feedback, newest first.</p>
                  <div className="inline-flex rounded-full border border-border bg-card p-0.5" data-testid="feedback-filter">
                    {["all", "new", "read", "resolved"].map((s) => (
                      <button
                        key={s}
                        data-testid={`feedback-filter-${s}`}
                        onClick={() => setFeedbackFilter(s)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize transition-colors ${feedbackFilter === s ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                {feedbackLoading && <p className="text-sm text-muted-foreground" data-testid="feedback-loading">Loading…</p>}
                {(() => {
                  if (feedbackLoading || !feedbackItems) return null;
                  const shown = feedbackItems.filter((f) => feedbackFilter === "all" || (f.status || "new") === feedbackFilter);
                  if (shown.length === 0) {
                    return <p className="text-sm text-muted-foreground" data-testid="feedback-empty">No {feedbackFilter === "all" ? "" : feedbackFilter + " "}feedback{feedbackFilter === "all" ? " yet" : ""}.</p>;
                  }
                  return (
                    <div className="space-y-2" data-testid="feedback-list">
                      {shown.map((f) => {
                        const status = f.status || "new";
                        return (
                          <div key={f.id} data-testid={`feedback-item-${f.id}`} className={`rounded-xl border p-3 ${status === "resolved" ? "border-border bg-muted/40 opacity-80" : "border-border"}`}>
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <div className="flex items-center gap-1.5">
                                <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                                  f.category === "bug" ? "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300"
                                  : f.category === "idea" ? "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
                                  : "bg-muted text-muted-foreground"
                                }`}>{f.category}</span>
                                <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                                  status === "new" ? "bg-primary/15 text-primary"
                                  : status === "read" ? "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
                                  : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                                }`} data-testid={`feedback-status-${f.id}`}>{status}</span>
                              </div>
                              <span className="text-xs text-muted-foreground">{new Date(f.created_at).toLocaleDateString()}</span>
                            </div>
                            <p className="text-sm whitespace-pre-wrap break-words">{f.message}</p>
                            <div className="flex items-center justify-between gap-2 mt-2">
                              <p className="text-xs text-muted-foreground truncate">{f.name} · {f.email}</p>
                              <div className="flex items-center gap-1 shrink-0">
                                {status === "new" && (
                                  <Button size="sm" variant="ghost" data-testid={`feedback-mark-read-${f.id}`} onClick={() => setFeedbackStatus(f.id, "read")} className="h-7 rounded-full text-xs">Mark read</Button>
                                )}
                                {status !== "resolved" ? (
                                  <Button size="sm" variant="ghost" data-testid={`feedback-resolve-${f.id}`} onClick={() => setFeedbackStatus(f.id, "resolved")} className="h-7 rounded-full text-xs text-emerald-600 hover:text-emerald-700">Resolve</Button>
                                ) : (
                                  <Button size="sm" variant="ghost" data-testid={`feedback-reopen-${f.id}`} onClick={() => setFeedbackStatus(f.id, "read")} className="h-7 rounded-full text-xs">Reopen</Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}
            {activeTab === "length" && (
              <div className="space-y-2" data-testid="tab-panel-length">
                <p className="text-sm text-muted-foreground">The maximum characters a root code can be. Applies to new and edited roots.</p>
                <div className="flex items-center gap-3 pt-2">
                  <Slider
                    data-testid="root-length-slider"
                    min={15}
                    max={30}
                    step={1}
                    value={[maxCodeLen]}
                    onValueChange={(v) => setDraft((d) => ({ ...d, max_code_len: v[0] }))}
                    className="flex-1"
                  />
                  <span data-testid="root-length-value" className="w-14 text-center text-sm font-bold font-mono rounded-lg border border-border bg-muted px-2 py-1">{maxCodeLen}</span>
                </div>
              </div>
            )}

            {activeTab === "alerts" && (
              <div className="space-y-2" data-testid="tab-panel-alerts">
                <p className="text-sm text-muted-foreground">How Carrot pings you when a snoozed root returns to your Field.</p>
                <div className="grid grid-cols-3 gap-2 pt-1" data-testid="nudge-group">
                  {NUDGE_OPTS.map((o) => (
                    <button
                      key={o.key}
                      data-testid={`nudge-${o.key}`}
                      onClick={() => setNudge(o.key)}
                      className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-2.5 text-xs font-semibold transition-colors ${
                        nudge === o.key ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <o.icon className="w-4 h-4" /> {o.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "snooze" && (
              <div className="space-y-2" data-testid="tab-panel-snooze">
                <p className="text-sm text-muted-foreground">Toggle presets on/off and drag to reorder how they appear in the menu.</p>
                <div className="rounded-xl border border-border divide-y divide-border" data-testid="snooze-list">
                  {orderedKeys.map((key) => {
                    const item = byKey[key];
                    const on = !disabled.includes(key);
                    return (
                      <div
                        key={key}
                        data-testid={`snooze-item-${key}`}
                        draggable
                        onDragStart={() => setDragKey(key)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => onDrop(key)}
                        className={`flex items-center gap-2 px-2.5 py-2 bg-card ${dragKey === key ? "opacity-50" : ""}`}
                      >
                        <GripVertical className="w-4 h-4 text-muted-foreground/60 cursor-grab shrink-0" data-testid={`drag-${key}`} />
                        <div className="min-w-0 flex-1">
                          <span className={`text-sm ${on ? "" : "text-muted-foreground line-through"}`}>{item.label}</span>
                          {item.isCustom && <span className="ml-2 text-[10px] uppercase tracking-wide text-primary font-bold">custom</span>}
                        </div>
                        {item.isCustom ? (
                          <Button size="icon" variant="ghost" onClick={() => removeCustom(item.raw.id)} data-testid={`remove-custom-${item.raw.id}`} className="h-8 w-8 rounded-full text-muted-foreground hover:text-destructive shrink-0">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Switch checked={on} onCheckedChange={(v) => toggleDefault(key, v)} data-testid={`toggle-snooze-${key}`} />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Add custom */}
                <div className="rounded-xl border border-dashed border-border p-3 space-y-2.5 mt-3" data-testid="add-custom-snooze">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Add a custom snooze</div>
                  <Select value={kind} onValueChange={setKind}>
                    <SelectTrigger data-testid="custom-kind-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="relative">Relative duration</SelectItem>
                      <SelectItem value="time">Specific time of day</SelectItem>
                    </SelectContent>
                  </Select>

                  {kind === "relative" ? (
                    <div className="flex items-center gap-2">
                      <Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-20" data-testid="custom-amount-input" />
                      <Select value={unit} onValueChange={setUnit}>
                        <SelectTrigger className="flex-1" data-testid="custom-unit-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="minutes">minutes</SelectItem>
                          <SelectItem value="hours">hours</SelectItem>
                          <SelectItem value="days">days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Label className="text-xs text-muted-foreground">Days ahead</Label>
                        <Input type="number" min="0" value={daysAhead} onChange={(e) => setDaysAhead(e.target.value)} data-testid="custom-days-input" />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs text-muted-foreground">Time</Label>
                        <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="custom-time-input" />
                      </div>
                    </div>
                  )}

                  <Input placeholder={`Label (optional) — e.g. "${autoLabel()}"`} value={label} onChange={(e) => setLabel(e.target.value)} data-testid="custom-label-input" />
                  <Button onClick={addCustom} data-testid="add-custom-btn" className="w-full bg-primary hover:bg-[#E85D04] text-white font-semibold gap-1.5">
                    <Plus className="w-4 h-4" /> Add snooze option
                  </Button>
                </div>
              </div>
            )}

            {activeTab === "categories" && (
              <div className="space-y-2" data-testid="tab-panel-categories">
                <p className="text-sm text-muted-foreground">Optional labels you can assign to roots when you plant them.</p>
                {categories.length > 0 && (
                  <div className="rounded-xl border border-border divide-y divide-border" data-testid="category-list">
                    {categories.map((c) => (
                      <div key={c.id} data-testid={`category-item-${c.id}`} className="flex items-center justify-between px-3 py-2">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full border ${categoryChipCls(c.color)}`}><Tag className="w-3 h-3" /> {c.name}</span>
                        <Button size="icon" variant="ghost" onClick={() => removeCategory(c.id)} data-testid={`remove-category-${c.id}`} className="h-8 w-8 rounded-full text-muted-foreground hover:text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2 pt-1">
                  <Input
                    data-testid="new-category-input"
                    placeholder="New category, e.g. Client work"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") addCategoryLocal(); }}
                  />
                  <Button onClick={addCategoryLocal} data-testid="add-category-btn" className="bg-primary hover:bg-[#E85D04] text-white font-semibold gap-1.5 shrink-0">
                    <Plus className="w-4 h-4" /> Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 pt-0.5">
                  {CATEGORY_COLOR_KEYS.map((k) => (
                    <button
                      key={k}
                      data-testid={`newcat-color-${k}`}
                      onClick={() => setNewCatColor(k)}
                      className={`w-6 h-6 rounded-full ${categoryDotCls(k)} transition-transform ${newCatColor === k ? "ring-2 ring-offset-2 ring-foreground scale-110" : "hover:scale-105"}`}
                      title={k}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t border-border sm:justify-between items-center gap-2">
            <span className="text-xs text-muted-foreground" data-testid="settings-dirty-hint">
              {dirty ? "You have unsaved changes" : "All changes saved"}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={attemptClose} data-testid="settings-cancel-btn" className="rounded-full">Cancel</Button>
              <Button onClick={handleSave} disabled={!dirty} data-testid="settings-save-btn" className="rounded-full bg-primary hover:bg-[#E85D04] text-white font-semibold gap-1.5">
                <Check className="w-4 h-4" /> Save changes
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="settings-discard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You've made changes to your settings that haven't been saved yet. Discard them and close?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="discard-cancel-btn">Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={discardAndClose} data-testid="discard-confirm-btn" className="bg-destructive hover:bg-destructive/90 text-white">
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
